import { MapPin, Bell, Camera, Check } from 'lucide-react';

interface PermissionsScreenProps {
  onComplete: () => void;
}

export function PermissionsScreen({ onComplete }: PermissionsScreenProps) {
  const permissions = [
    {
      icon: MapPin,
      title: 'Location Access',
      description: 'Required for job matching, navigation, and tracking your deliveries',
      required: true,
    },
    {
      icon: Bell,
      title: 'Notifications',
      description: 'Get instant alerts for new jobs, updates, and earnings',
      required: true,
    },
    {
      icon: Camera,
      title: 'Camera',
      description: 'Capture proof of pickup and delivery photos',
      required: false,
    },
  ];

  return (
    <div className="absolute inset-0 bg-white flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {/* Header */}
        <div className="p-8 pt-16">
          <div className="w-16 h-16 rounded-full bg-[#00B277]/10 flex items-center justify-center mb-6">
            <Check className="w-8 h-8 text-[#00B277]" />
          </div>
          
          <h1 className="text-[28px] text-[#111111] mb-2">
            Enable permissions
          </h1>
          
          <p className="text-[#6B6B6B]">
            To provide the best experience, we need access to the following:
          </p>
        </div>

        {/* Permissions List */}
        <div className="px-8 space-y-4">
          {permissions.map((permission, index) => {
            const Icon = permission.icon;
            return (
              <div
                key={index}
                className="bg-gray-50 rounded-[12px] p-4 border border-gray-200"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-[#E0001B]" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-[16px] text-[#111111]">{permission.title}</h3>
                      {permission.required && (
                        <span className="px-2 py-0.5 bg-[#E0001B]/10 text-[#E0001B] rounded text-[10px]">
                          Required
                        </span>
                      )}
                    </div>
                    <p className="text-[14px] text-[#6B6B6B]">
                      {permission.description}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        <div className="px-8 mt-6">
          <div className="bg-blue-50 rounded-[12px] p-4 border border-blue-100">
            <p className="text-[14px] text-blue-900">
              💡 You can change these permissions anytime in your device settings
            </p>
          </div>
        </div>
      </div>

      {/* Bottom Button */}
      <div className="p-8 border-t border-gray-100">
        <button
          onClick={async () => {
            // Request geolocation permission
            if ('geolocation' in navigator) {
              navigator.geolocation.getCurrentPosition(
                () => console.log('✅ Location permission granted'),
                (err) => console.warn('⚠️ Location permission denied:', err.message)
              );
            }
            // Request notification permission
            if ('Notification' in window && Notification.permission === 'default') {
              try {
                const result = await Notification.requestPermission();
                console.log('🔔 Notification permission:', result);
              } catch (_) {}
            }
            onComplete();
          }}
          className="w-full h-12 rounded-[12px] bg-[#E0001B] text-white hover:bg-[#C00017] transition-colors shadow-sm"
        >
          Allow & Continue
        </button>
      </div>
    </div>
  );
}
